import type { BenchmarkRow } from "@/components/BenchmarkTable";

export interface BenchmarkData {
  title: string;
  description?: string;
  source?: string;
  sourceYear?: string;
  columns: { key: string; label: string; highlight?: boolean }[];
  data: BenchmarkRow[];
}

// Email deliverability benchmarks
export const emailDeliverabilityBenchmarks: Record<string, BenchmarkData[]> = {
  "summer-email-drop": [
    {
      title: "Out-of-Office Reply Rate by Season (ZoomInfo)",
      description: "Share of outbound emails that received an out-of-office auto-reply, based on ZoomInfo's analysis of millions of sent emails. The summer bump is real but modest — the true spike is Christmas week.",
      source: "ZoomInfo — Out-of-Office Reply Rates by Season",
      sourceYear: "2023-2024",
      columns: [
        { key: "period", label: "Period" },
        { key: "oooRate", label: "OOO Reply Rate", highlight: true },
        { key: "vsMedian", label: "vs. Yearly Median (~2.7%)" },
      ],
      data: [
        { period: "Yearly median", oooRate: "~2.7%", vsMedian: "baseline" },
        { period: "Summer (Jun–Aug)", oooRate: "~3%", vsMedian: "+0.3 pts" },
        { period: "Christmas week", oooRate: "~6.8%", vsMedian: "+4.1 pts" },
      ],
    },
    {
      title: "Regional Summer Holiday Patterns (qualitative)",
      description: "When B2B outreach feels slowest by region. Statutory holiday norms are reliable; precise country-level engagement deltas are not well sourced, so they are not quoted here.",
      source: "OECD vacation norms; Eurostat working-time data; market commentary",
      sourceYear: "2023-2025",
      columns: [
        { key: "region", label: "Region" },
        { key: "peakWeeks", label: "Peak Slowdown Window" },
        { key: "vacationNorm", label: "Typical Leave Pattern" },
      ],
      data: [
        { region: "United States", peakWeeks: "Jul 1–15 (Independence Day + PTO)", vacationNorm: "3–7 days, scattered" },
        { region: "UK / Ireland", peakWeeks: "Aug 1–31", vacationNorm: "1–2 weeks" },
        { region: "France / Italy / Spain", peakWeeks: "Aug 1–31 (widespread office closures)", vacationNorm: "2–4 weeks" },
        { region: "Germany / Austria", peakWeeks: "Jul 15 – Aug 31", vacationNorm: "2–3 weeks" },
        { region: "Scandinavia", peakWeeks: "Jul 1 – Aug 15", vacationNorm: "3–5 weeks" },
      ],
    },
  ],
  "december-email-drop": [
    {
      title: "December Email Performance Benchmarks",
      description: "Holiday season email metrics across major providers",
      source: "Validity Deliverability Benchmark",
      sourceYear: "2024",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "november", label: "November" },
        { key: "december", label: "December" },
        { key: "change", label: "Change", highlight: true },
      ],
      data: [
        { metric: "Global Inbox Rate", november: "87.2%", december: "84.1%", change: "-3.1%" },
        { metric: "Open Rate", november: "21.5%", december: "18.3%", change: "-3.2%" },
        { metric: "Click Rate", november: "2.8%", december: "2.1%", change: "-0.7%" },
        { metric: "Conversion Rate", november: "6.1%", december: "2.9%", change: "-3.2%" },
        { metric: "Unsubscribe Rate", november: "0.11%", december: "0.22%", change: "+0.11%" },
      ],
    },
    {
      title: "Provider-Specific Inbox Placement",
      description: "Inbox placement rates by email provider",
      source: "Validity",
      sourceYear: "2024",
      columns: [
        { key: "provider", label: "Provider" },
        { key: "inboxRate", label: "Inbox Rate" },
        { key: "spamRate", label: "Spam Rate" },
        { key: "missingRate", label: "Missing" },
      ],
      data: [
        { provider: "Gmail", inboxRate: "87.2%", spamRate: "8.4%", missingRate: "4.4%" },
        { provider: "Microsoft 365", inboxRate: "75.6%", spamRate: "15.2%", missingRate: "9.2%" },
        { provider: "Yahoo", inboxRate: "82.1%", spamRate: "11.3%", missingRate: "6.6%" },
        { provider: "Apple Mail", inboxRate: "89.5%", spamRate: "6.2%", missingRate: "4.3%" },
      ],
    },
  ],
  "cold-email-deliverability": [
    {
      title: "Cold Email Volume vs Performance",
      description: "How daily sending volume impacts deliverability",
      source: "Instantly, Lemlist, Woodpecker",
      sourceYear: "2024-2025",
      columns: [
        { key: "volume", label: "Emails/Day" },
        { key: "openRate", label: "Open Rate", highlight: true },
        { key: "replyRate", label: "Reply Rate" },
        { key: "riskLevel", label: "Risk Level" },
      ],
      data: [
        { volume: "1-25", openRate: "70%+", replyRate: "5-8%", riskLevel: "Low" },
        { volume: "25-50", openRate: "65%", replyRate: "4-6%", riskLevel: "Low" },
        { volume: "50-100", openRate: "55-60%", replyRate: "3-5%", riskLevel: "Medium" },
        { volume: "100-200", openRate: "40-50%", replyRate: "2-3%", riskLevel: "High" },
        { volume: "200+", openRate: "20-30%", replyRate: "<1%", riskLevel: "Critical" },
      ],
    },
    {
      title: "Spam Filter Trigger Factors",
      description: "Weighted impact of different spam triggers",
      source: "GetResponse, Validity",
      sourceYear: "2024",
      columns: [
        { key: "factor", label: "Factor" },
        { key: "impact", label: "Impact Weight", highlight: true },
        { key: "description", label: "Description" },
      ],
      data: [
        { factor: "Sending Volume", impact: "35%", description: "Too many emails too fast" },
        { factor: "Content Patterns", impact: "25%", description: "Spammy words, links, formatting" },
        { factor: "Engagement Signals", impact: "20%", description: "Low opens, no replies" },
        { factor: "Domain Reputation", impact: "15%", description: "New or damaged sender score" },
        { factor: "Technical Setup", impact: "5%", description: "Missing SPF, DKIM, DMARC" },
      ],
    },
  ],
  "spf-dkim-dmarc-guide": [
    {
      title: "Email Authentication Protocol Comparison",
      description: "SPF, DKIM, and DMARC feature comparison",
      source: "Google, Microsoft",
      sourceYear: "2024",
      columns: [
        { key: "feature", label: "Feature" },
        { key: "spf", label: "SPF" },
        { key: "dkim", label: "DKIM" },
        { key: "dmarc", label: "DMARC" },
      ],
      data: [
        { feature: "Verifies Sender IP", spf: "✓", dkim: "✗", dmarc: "✗" },
        { feature: "Cryptographic Signing", spf: "✗", dkim: "✓", dmarc: "✗" },
        { feature: "Policy Enforcement", spf: "✗", dkim: "✗", dmarc: "✓" },
        { feature: "Reporting", spf: "✗", dkim: "✗", dmarc: "✓" },
        { feature: "Prevents Spoofing", spf: "Partial", dkim: "Partial", dmarc: "Full" },
      ],
    },
    {
      title: "DMARC Adoption by Industry",
      description: "Percentage of domains with valid DMARC records",
      source: "Valimail, DMARC.org",
      sourceYear: "2024",
      columns: [
        { key: "industry", label: "Industry" },
        { key: "adoption", label: "DMARC Adoption", highlight: true },
        { key: "enforcement", label: "With Enforcement" },
      ],
      data: [
        { industry: "Technology", adoption: "68%", enforcement: "42%" },
        { industry: "Financial Services", adoption: "72%", enforcement: "51%" },
        { industry: "Healthcare", adoption: "45%", enforcement: "22%" },
        { industry: "Retail", adoption: "52%", enforcement: "28%" },
        { industry: "Government", adoption: "81%", enforcement: "67%" },
      ],
    },
  ],
  "domain-warmup-strategies": [
    {
      title: "8-Week Cold Email Warmup Schedule",
      description: "Recommended daily volumes per mailbox",
      source: "Instantly, Lemlist, Woodpecker",
      sourceYear: "2024-2025",
      columns: [
        { key: "week", label: "Week" },
        { key: "dailyVolume", label: "Daily Volume" },
        { key: "weeklyTotal", label: "Weekly Total" },
        { key: "focus", label: "Focus" },
      ],
      data: [
        { week: "Week 1", dailyVolume: "5-10", weeklyTotal: "35-70", focus: "Engaged contacts only" },
        { week: "Week 2", dailyVolume: "10-15", weeklyTotal: "70-105", focus: "Add recent engagers" },
        { week: "Week 3", dailyVolume: "15-25", weeklyTotal: "105-175", focus: "Expand to warm leads" },
        { week: "Week 4", dailyVolume: "25-35", weeklyTotal: "175-245", focus: "Broader prospect list" },
        { week: "Week 5", dailyVolume: "35-50", weeklyTotal: "245-350", focus: "Scale carefully" },
        { week: "Week 6", dailyVolume: "50-60", weeklyTotal: "350-420", focus: "Approaching limit" },
        { week: "Week 7", dailyVolume: "60-70", weeklyTotal: "420-490", focus: "Near full capacity" },
        { week: "Week 8", dailyVolume: "70-80", weeklyTotal: "490-560", focus: "Warmup complete" },
      ],
    },
  ],
  "sending-time-spam-filters": [
    {
      title: "Email Volume vs Spam Rate",
      description: "How monthly sending volume correlates with spam placement",
      source: "GetResponse Email Benchmarks",
      sourceYear: "2024",
      columns: [
        { key: "volume", label: "Monthly Volume" },
        { key: "spamRate", label: "Spam Rate", highlight: true },
        { key: "inboxRate", label: "Inbox Rate" },
      ],
      data: [
        { volume: "< 10,000", spamRate: "18.2%", inboxRate: "81.8%" },
        { volume: "10,000 - 50,000", spamRate: "22.4%", inboxRate: "77.6%" },
        { volume: "50,000 - 200,000", spamRate: "29.3%", inboxRate: "70.7%" },
        { volume: "200,000 - 1M", spamRate: "24.1%", inboxRate: "75.9%" },
        { volume: "1M+", spamRate: "16.2%", inboxRate: "83.8%" },
      ],
    },
  ],
  "gmail-promotions-tab": [
    {
      title: "Gmail Tab Engagement Comparison",
      description: "User behavior across Gmail inbox tabs",
      source: "Validity, Litmus",
      sourceYear: "2024",
      columns: [
        { key: "tab", label: "Tab" },
        { key: "checkRate", label: "Check Regularly" },
        { key: "openRate", label: "Avg Open Rate", highlight: true },
        { key: "clickRate", label: "Avg Click Rate" },
      ],
      data: [
        { tab: "Primary", checkRate: "98%", openRate: "22.8%", clickRate: "3.2%" },
        { tab: "Promotions", checkRate: "68.8%", openRate: "19.2%", clickRate: "2.4%" },
        { tab: "Social", checkRate: "45%", openRate: "15.1%", clickRate: "1.8%" },
        { tab: "Updates", checkRate: "72%", openRate: "28.4%", clickRate: "4.1%" },
      ],
    },
  ],
  "reply-based-deliverability": [
    {
      title: "Engagement Signal Weighting by ISP",
      description: "How major providers weight different engagement metrics",
      source: "Validity, SparkPost Research",
      sourceYear: "2025",
      columns: [
        { key: "signal", label: "Engagement Signal" },
        { key: "gmail", label: "Gmail" },
        { key: "microsoft", label: "Microsoft" },
        { key: "yahoo", label: "Yahoo", highlight: true },
      ],
      data: [
        { signal: "Reply", gmail: "Very High", microsoft: "High", yahoo: "Very High" },
        { signal: "Forward", gmail: "High", microsoft: "Medium", yahoo: "High" },
        { signal: "Add to Contacts", gmail: "Very High", microsoft: "High", yahoo: "Medium" },
        { signal: "Open (tracked)", gmail: "Low", microsoft: "Medium", yahoo: "Low" },
        { signal: "Click", gmail: "Medium", microsoft: "Medium", yahoo: "Medium" },
        { signal: "Mark as Important", gmail: "High", microsoft: "N/A", yahoo: "N/A" },
      ],
    },
    {
      title: "Reply Rate Benchmarks by Industry",
      description: "Average cold email reply rates across sectors",
      source: "Instantly, QuickMail, Lemlist",
      sourceYear: "2025",
      columns: [
        { key: "industry", label: "Industry" },
        { key: "avgReply", label: "Avg Reply Rate", highlight: true },
        { key: "topPerformer", label: "Top 10%" },
        { key: "bottomPerformer", label: "Bottom 10%" },
      ],
      data: [
        { industry: "SaaS/Tech", avgReply: "6.2%", topPerformer: "15%+", bottomPerformer: "<1%" },
        { industry: "Agency/Consulting", avgReply: "8.4%", topPerformer: "18%+", bottomPerformer: "<2%" },
        { industry: "Financial Services", avgReply: "4.1%", topPerformer: "10%+", bottomPerformer: "<1%" },
        { industry: "E-commerce", avgReply: "3.8%", topPerformer: "9%+", bottomPerformer: "<1%" },
        { industry: "Healthcare", avgReply: "5.2%", topPerformer: "12%+", bottomPerformer: "<1%" },
      ],
    },
  ],
  "zero-trust-email-dmarc": [
    {
      title: "DMARC Policy Adoption Trends",
      description: "Global DMARC enforcement levels over time",
      source: "Valimail, DMARC.org",
      sourceYear: "2025",
      columns: [
        { key: "policy", label: "DMARC Policy" },
        { key: "2023", label: "2023" },
        { key: "2024", label: "2024" },
        { key: "2025", label: "2025", highlight: true },
      ],
      data: [
        { policy: "p=none (monitor)", "2023": "58%", "2024": "45%", "2025": "28%" },
        { policy: "p=quarantine", "2023": "24%", "2024": "28%", "2025": "32%" },
        { policy: "p=reject", "2023": "18%", "2024": "27%", "2025": "40%" },
      ],
    },
    {
      title: "Email Authentication Failure Impact",
      description: "What happens to emails failing authentication under strict enforcement",
      source: "Google Postmaster Tools, Microsoft SNDS",
      sourceYear: "2025",
      columns: [
        { key: "failureType", label: "Failure Type" },
        { key: "preEnforcement", label: "Pre-2026" },
        { key: "postEnforcement", label: "Post-2026", highlight: true },
      ],
      data: [
        { failureType: "SPF fail, no DKIM", preEnforcement: "Likely spam", postEnforcement: "Rejected" },
        { failureType: "DKIM fail, SPF pass", preEnforcement: "Delivered", postEnforcement: "Rejected" },
        { failureType: "Relaxed alignment only", preEnforcement: "Delivered", postEnforcement: "Rejected" },
        { failureType: "No DMARC record", preEnforcement: "Delivered", postEnforcement: "High spam risk" },
        { failureType: "p=none policy", preEnforcement: "Delivered", postEnforcement: "May be rejected" },
      ],
    },
  ],
};

// AI & Development benchmarks
export const aiDevelopmentBenchmarks: Record<string, BenchmarkData[]> = {
  "ai-coding-assistants": [
    {
      title: "AI Coding Assistant Comparison",
      description: "Feature and pricing comparison of top AI coding tools",
      source: "GitHub, Cursor, Anthropic",
      sourceYear: "2025",
      columns: [
        { key: "tool", label: "Tool" },
        { key: "price", label: "Price/Month" },
        { key: "models", label: "AI Models" },
        { key: "bestFor", label: "Best For" },
      ],
      data: [
        { tool: "GitHub Copilot", price: "$10-19", models: "GPT-4, Claude", bestFor: "General coding" },
        { tool: "Cursor", price: "$20", models: "Claude 3.5, GPT-4", bestFor: "Full IDE experience" },
        { tool: "Codeium", price: "Free-$12", models: "Custom", bestFor: "Budget-conscious" },
        { tool: "Tabnine", price: "$12-39", models: "Custom + GPT", bestFor: "Enterprise/Privacy" },
        { tool: "Amazon CodeWhisperer", price: "Free-$19", models: "Amazon", bestFor: "AWS development" },
      ],
    },
    {
      title: "AI Coding Productivity Impact",
      description: "Developer productivity metrics with AI assistance",
      source: "GitHub Copilot Research",
      sourceYear: "2024",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "withAI", label: "With AI", highlight: true },
        { key: "withoutAI", label: "Without AI" },
        { key: "improvement", label: "Improvement" },
      ],
      data: [
        { metric: "Task Completion Speed", withAI: "55% faster", withoutAI: "Baseline", improvement: "+55%" },
        { metric: "Code Acceptance Rate", withAI: "30%", withoutAI: "N/A", improvement: "30% suggestions used" },
        { metric: "Developer Satisfaction", withAI: "92%", withoutAI: "73%", improvement: "+19%" },
        { metric: "Time on Boilerplate", withAI: "35%", withoutAI: "60%", improvement: "-25%" },
      ],
    },
  ],
  "vibe-coding-guide": [
    {
      title: "Vibe Coding Platform Comparison",
      description: "AI-first development platforms for rapid prototyping",
      source: "Platform documentation",
      sourceYear: "2025",
      columns: [
        { key: "platform", label: "Platform" },
        { key: "type", label: "Type" },
        { key: "bestFor", label: "Best For" },
        { key: "learning", label: "Learning Curve" },
      ],
      data: [
        { platform: "Windsurf", type: "AI IDE", bestFor: "Full-stack apps", learning: "Low" },
        { platform: "Bolt.new", type: "AI Builder", bestFor: "Quick prototypes", learning: "Low" },
        { platform: "Cursor", type: "AI IDE", bestFor: "Custom code", learning: "Medium" },
        { platform: "v0", type: "UI Generator", bestFor: "Component design", learning: "Low" },
        { platform: "Replit", type: "Cloud IDE + AI", bestFor: "Learning/prototyping", learning: "Low" },
      ],
    },
  ],
  "prompt-engineering-developers": [
    {
      title: "Prompt Engineering Techniques Effectiveness",
      description: "Impact of different prompting strategies on output quality",
      source: "OpenAI, Anthropic Research",
      sourceYear: "2024",
      columns: [
        { key: "technique", label: "Technique" },
        { key: "effectiveness", label: "Effectiveness", highlight: true },
        { key: "useCase", label: "Best Use Case" },
      ],
      data: [
        { technique: "Chain of Thought", effectiveness: "High", useCase: "Complex reasoning" },
        { technique: "Few-Shot Examples", effectiveness: "High", useCase: "Format consistency" },
        { technique: "Role Prompting", effectiveness: "Medium", useCase: "Expertise domains" },
        { technique: "Step-by-Step", effectiveness: "High", useCase: "Multi-step tasks" },
        { technique: "Temperature Tuning", effectiveness: "Medium", useCase: "Creativity vs precision" },
      ],
    },
  ],
  "ai-agents-automation": [
    {
      title: "AI Agent Platform Comparison",
      description: "No-code and low-code automation platforms",
      source: "Platform documentation",
      sourceYear: "2025",
      columns: [
        { key: "platform", label: "Platform" },
        { key: "type", label: "Type" },
        { key: "pricing", label: "Starting Price" },
        { key: "bestFor", label: "Best For" },
      ],
      data: [
        { platform: "n8n", type: "Open Source", pricing: "Free / $20", bestFor: "Technical teams" },
        { platform: "Make (Integromat)", type: "Visual Builder", pricing: "$9/mo", bestFor: "Complex workflows" },
        { platform: "Zapier", type: "No-Code", pricing: "$19.99/mo", bestFor: "Simple automations" },
        { platform: "Langchain", type: "Code Framework", pricing: "Free", bestFor: "Custom AI agents" },
        { platform: "AutoGPT", type: "Autonomous", pricing: "Free", bestFor: "Experimental" },
      ],
    },
  ],
  "llm-integration-guide": [
    {
      title: "LLM Provider Comparison",
      description: "Major LLM providers for application integration",
      source: "Provider documentation",
      sourceYear: "2025",
      columns: [
        { key: "provider", label: "Provider" },
        { key: "topModel", label: "Top Model" },
        { key: "pricing", label: "Pricing (per 1M tokens)" },
        { key: "strengths", label: "Strengths" },
      ],
      data: [
        { provider: "OpenAI", topModel: "GPT-4o", pricing: "$2.50-$10", strengths: "General purpose" },
        { provider: "Anthropic", topModel: "Claude 3.5", pricing: "$3-$15", strengths: "Safety, long context" },
        { provider: "Google", topModel: "Gemini 2.0", pricing: "$1.25-$5", strengths: "Multimodal" },
        { provider: "Mistral", topModel: "Large 2", pricing: "$2-$6", strengths: "Cost efficiency" },
        { provider: "Meta", topModel: "Llama 3.1", pricing: "Free (self-host)", strengths: "Open source" },
      ],
    },
  ],
  "geo-vs-seo": [
    {
      title: "Search Traffic Distribution (2025)",
      description: "How search queries are distributed across platforms",
      source: "SparkToro, Similarweb",
      sourceYear: "2025",
      columns: [
        { key: "platform", label: "Platform" },
        { key: "share", label: "Query Share", highlight: true },
        { key: "growth", label: "YoY Growth" },
      ],
      data: [
        { platform: "Google Search", share: "58%", growth: "-8%" },
        { platform: "ChatGPT", share: "18%", growth: "+340%" },
        { platform: "Perplexity", share: "7%", growth: "+520%" },
        { platform: "Google AI Overviews", share: "12%", growth: "+180%" },
        { platform: "Bing + Copilot", share: "5%", growth: "+45%" },
      ],
    },
    {
      title: "GEO vs SEO Optimization Factors",
      description: "Key ranking signals for each channel",
      source: "Industry research, AI provider documentation",
      sourceYear: "2025",
      columns: [
        { key: "factor", label: "Optimization Factor" },
        { key: "seoImpact", label: "SEO Impact" },
        { key: "geoImpact", label: "GEO Impact", highlight: true },
      ],
      data: [
        { factor: "Backlink quantity", seoImpact: "Very High", geoImpact: "Low" },
        { factor: "FAQ structured data", seoImpact: "Medium", geoImpact: "Very High" },
        { factor: "Dataset schema", seoImpact: "Low", geoImpact: "Very High" },
        { factor: "Keyword density", seoImpact: "Medium", geoImpact: "Low" },
        { factor: "Definitive statements", seoImpact: "Low", geoImpact: "Very High" },
        { factor: "Domain authority", seoImpact: "Very High", geoImpact: "Medium" },
        { factor: "Content freshness", seoImpact: "Medium", geoImpact: "High" },
      ],
    },
  ],
};

// Security benchmarks
export const securityBenchmarks: Record<string, BenchmarkData[]> = {
  "api-security-best-practices": [
    {
      title: "OWASP API Security Top 10 (2023)",
      description: "Most critical API security risks",
      source: "OWASP Foundation",
      sourceYear: "2023",
      columns: [
        { key: "rank", label: "#" },
        { key: "vulnerability", label: "Vulnerability" },
        { key: "prevalence", label: "Prevalence", highlight: true },
        { key: "impact", label: "Impact" },
      ],
      data: [
        { rank: "1", vulnerability: "Broken Object Level Authorization", prevalence: "Very High", impact: "Critical" },
        { rank: "2", vulnerability: "Broken Authentication", prevalence: "High", impact: "Critical" },
        { rank: "3", vulnerability: "Broken Object Property Level Auth", prevalence: "High", impact: "High" },
        { rank: "4", vulnerability: "Unrestricted Resource Consumption", prevalence: "Medium", impact: "High" },
        { rank: "5", vulnerability: "Broken Function Level Authorization", prevalence: "Medium", impact: "Critical" },
      ],
    },
    {
      title: "API Security Statistics",
      description: "Current state of API security landscape",
      source: "Salt Security, Gartner",
      sourceYear: "2024",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "value", label: "Value", highlight: true },
        { key: "context", label: "Context" },
      ],
      data: [
        { metric: "APIs with vulnerabilities", value: "94%", context: "Production applications" },
        { metric: "Attack increase (YoY)", value: "681%", context: "Malicious API traffic" },
        { metric: "Avg breach detection", value: "287 days", context: "Time to identify" },
        { metric: "API attacks share", value: "5%+", context: "Of all web traffic" },
      ],
    },
  ],
  "secure-coding-practices": [
    {
      title: "Common Vulnerability Types",
      description: "Most frequent security issues in modern applications",
      source: "OWASP, Snyk",
      sourceYear: "2024",
      columns: [
        { key: "vulnerability", label: "Vulnerability" },
        { key: "frequency", label: "Frequency", highlight: true },
        { key: "severity", label: "Severity" },
        { key: "prevention", label: "Prevention" },
      ],
      data: [
        { vulnerability: "Injection (SQL, NoSQL)", frequency: "Very Common", severity: "Critical", prevention: "Parameterized queries" },
        { vulnerability: "XSS (Cross-Site Scripting)", frequency: "Very Common", severity: "High", prevention: "Output encoding" },
        { vulnerability: "Broken Access Control", frequency: "Common", severity: "Critical", prevention: "RBAC, least privilege" },
        { vulnerability: "Security Misconfiguration", frequency: "Very Common", severity: "Medium", prevention: "Security hardening" },
        { vulnerability: "Cryptographic Failures", frequency: "Common", severity: "High", prevention: "Strong encryption" },
      ],
    },
  ],
  "phishing-attack-detection": [
    {
      title: "Phishing Attack Statistics",
      description: "Current phishing threat landscape",
      source: "Proofpoint, Verizon DBIR",
      sourceYear: "2024",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "value", label: "Value", highlight: true },
        { key: "source", label: "Source" },
      ],
      data: [
        { metric: "Attacks starting with phishing", value: "91%", source: "Deloitte" },
        { metric: "Organizations attacked", value: "83%", source: "Proofpoint" },
        { metric: "Avg time to click", value: "16 min", source: "Verizon DBIR" },
        { metric: "Human element in breaches", value: "74%", source: "Verizon DBIR" },
        { metric: "BEC losses (2023)", value: "$2.9B", source: "FBI IC3" },
      ],
    },
  ],
};

// Project management benchmarks
export const pmBenchmarks: Record<string, BenchmarkData[]> = {
  "project-management-comparison": [
    {
      title: "Project Management Tool Pricing (2025)",
      description: "Per-user monthly pricing comparison",
      source: "Official pricing pages",
      sourceYear: "2025",
      columns: [
        { key: "tool", label: "Tool" },
        { key: "free", label: "Free Tier" },
        { key: "starter", label: "Starter" },
        { key: "business", label: "Business", highlight: true },
      ],
      data: [
        { tool: "Linear", free: "250 issues", starter: "$8", business: "$14" },
        { tool: "Jira", free: "10 users", starter: "$7.53", business: "$13.53" },
        { tool: "Asana", free: "Basic", starter: "$10.99", business: "$24.99" },
        { tool: "Monday.com", free: "2 users", starter: "$9", business: "$16" },
        { tool: "ClickUp", free: "Forever", starter: "$7", business: "$12" },
        { tool: "Notion", free: "Personal", starter: "$8", business: "$15" },
      ],
    },
  ],
  "no-code-vs-code": [
    {
      title: "No-Code vs Traditional Development",
      description: "Comparison of development approaches",
      source: "Gartner, Forrester",
      sourceYear: "2024",
      columns: [
        { key: "factor", label: "Factor" },
        { key: "noCode", label: "No-Code" },
        { key: "lowCode", label: "Low-Code" },
        { key: "traditional", label: "Traditional", highlight: true },
      ],
      data: [
        { factor: "Time to MVP", noCode: "Days", lowCode: "Weeks", traditional: "Months" },
        { factor: "Developer skill needed", noCode: "Low", lowCode: "Medium", traditional: "High" },
        { factor: "Customization", noCode: "Limited", lowCode: "Moderate", traditional: "Unlimited" },
        { factor: "Scalability", noCode: "Limited", lowCode: "Good", traditional: "Excellent" },
        { factor: "Maintenance cost", noCode: "Low", lowCode: "Medium", traditional: "High" },
      ],
    },
  ],
};

// Business IT benchmarks
export const businessItBenchmarks: Record<string, BenchmarkData[]> = {
  "it-stakeholder-roles": [
    {
      title: "IT Leadership Salary Benchmarks",
      description: "Average base salary ranges for IT leadership roles (US)",
      source: "Glassdoor, LinkedIn Salary Insights",
      sourceYear: "2025",
      columns: [
        { key: "role", label: "Role" },
        { key: "median", label: "Median Salary", highlight: true },
        { key: "range", label: "Range" },
        { key: "growth", label: "YoY Growth" },
      ],
      data: [
        { role: "CTO", median: "$280,000", range: "$200K-$450K", growth: "+8%" },
        { role: "CIO", median: "$245,000", range: "$180K-$380K", growth: "+6%" },
        { role: "VP of Engineering", median: "$220,000", range: "$170K-$320K", growth: "+7%" },
        { role: "IT Director", median: "$165,000", range: "$130K-$220K", growth: "+5%" },
        { role: "DevOps Engineer", median: "$140,000", range: "$110K-$185K", growth: "+9%" },
        { role: "Engineering Manager", median: "$175,000", range: "$140K-$230K", growth: "+6%" },
      ],
    },
    {
      title: "IT Team Structure by Company Size",
      description: "Typical IT leadership roles at different company stages",
      source: "Gartner, IT Workforce Study",
      sourceYear: "2024",
      columns: [
        { key: "size", label: "Company Size" },
        { key: "employees", label: "Employees" },
        { key: "itLeadership", label: "IT Leadership Roles" },
        { key: "ratio", label: "IT:Total Ratio" },
      ],
      data: [
        { size: "Startup", employees: "<50", itLeadership: "CTO (combined)", ratio: "1:5" },
        { size: "Growth", employees: "50-200", itLeadership: "CTO + IT Manager", ratio: "1:8" },
        { size: "Scale-up", employees: "200-500", itLeadership: "CTO + IT Director", ratio: "1:10" },
        { size: "Enterprise", employees: "500-2000", itLeadership: "CTO + CIO + Directors", ratio: "1:12" },
        { size: "Large Enterprise", employees: "2000+", itLeadership: "Full C-suite + VPs", ratio: "1:15" },
      ],
    },
  ],
  "startup-failure-delegation": [
    {
      title: "Top Reasons Startups Fail",
      description: "CB Insights analysis of 110+ failed startups",
      source: "CB Insights",
      sourceYear: "2024",
      columns: [
        { key: "reason", label: "Failure Reason" },
        { key: "percentage", label: "Percentage", highlight: true },
        { key: "delegationImpact", label: "Delegation Impact" },
      ],
      data: [
        { reason: "Ran out of cash", percentage: "38%", delegationImpact: "High - lack of financial oversight" },
        { reason: "No market need", percentage: "35%", delegationImpact: "High - no time for customer validation" },
        { reason: "Got outcompeted", percentage: "20%", delegationImpact: "Medium - founder bottlenecks" },
        { reason: "Flawed business model", percentage: "19%", delegationImpact: "Medium - no strategic focus" },
        { reason: "Team problems", percentage: "23%", delegationImpact: "Very High - direct result" },
        { reason: "Burnout", percentage: "8%", delegationImpact: "Very High - direct result" },
      ],
    },
    {
      title: "Founder Burnout & Mental Health Statistics",
      description: "Entrepreneurship mental health research",
      source: "Harvard Business Review, NAMI",
      sourceYear: "2024",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "founders", label: "Founders", highlight: true },
        { key: "general", label: "General Population" },
      ],
      data: [
        { metric: "Report mental health challenges", founders: "72%", general: "20%" },
        { metric: "Experience burnout", founders: "63%", general: "28%" },
        { metric: "Work 60+ hours/week", founders: "81%", general: "8%" },
        { metric: "Take less than 2 weeks vacation/year", founders: "67%", general: "33%" },
        { metric: "Feel isolated in role", founders: "55%", general: "15%" },
      ],
    },
    {
      title: "Optimal Team Span of Control",
      description: "Management research on direct report limits",
      source: "McKinsey, Harvard Business Review",
      sourceYear: "2024",
      columns: [
        { key: "reports", label: "Direct Reports" },
        { key: "effectiveness", label: "Manager Effectiveness", highlight: true },
        { key: "recommendation", label: "Recommendation" },
      ],
      data: [
        { reports: "1-4", effectiveness: "High (time available)", recommendation: "Add responsibilities" },
        { reports: "5-7", effectiveness: "Optimal", recommendation: "Ideal range" },
        { reports: "8-10", effectiveness: "Declining", recommendation: "Consider restructuring" },
        { reports: "11-15", effectiveness: "Low", recommendation: "Add management layer" },
        { reports: "16+", effectiveness: "Critical risk", recommendation: "Immediate restructuring needed" },
      ],
    },
  ],
  "departmental-autonomy": [
    {
      title: "Impact of Skip-Level Communication on Manager Effectiveness",
      description: "Research on how bypassing managers affects organizational performance",
      source: "Gallup, Harvard Business Review",
      sourceYear: "2025",
      columns: [
        { key: "behavior", label: "Leadership Behavior" },
        { key: "impact", label: "Impact on Managers", highlight: true },
        { key: "stat", label: "Data" },
      ],
      data: [
        { behavior: "Assigns tasks directly to ICs", impact: "Manager authority undermined", stat: "67% report feeling bypassed" },
        { behavior: "Gives feedback to skip-levels", impact: "Manager credibility damaged", stat: "3x more likely to quit" },
        { behavior: "Asks ICs for status updates", impact: "Manager loses context", stat: "45% higher turnover" },
        { behavior: "Announces decisions to skip-levels first", impact: "Manager looks uninformed", stat: "58% feel 'out of the loop'" },
        { behavior: "Invites ICs to exec meetings without manager", impact: "Manager excluded from key discussions", stat: "72% report frustration" },
      ],
    },
    {
      title: "Chain of Command Compliance and Team Performance",
      description: "Outcomes when leaders respect vs bypass the hierarchy",
      source: "McKinsey, MIT Sloan",
      sourceYear: "2025",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "respect", label: "Chain Respected", highlight: true },
        { key: "bypass", label: "Chain Bypassed" },
      ],
      data: [
        { metric: "Manager retention (annual)", respect: "88%", bypass: "55%" },
        { metric: "Team decision speed", respect: "3x faster", bypass: "Baseline" },
        { metric: "Employee role clarity", respect: "91%", bypass: "47%" },
        { metric: "Conflicting priorities reported", respect: "12%", bypass: "68%" },
        { metric: "Manager effectiveness rating", respect: "4.2/5", bypass: "2.8/5" },
      ],
    },
  ],
};

// Freelance marketplace benchmarks
export const freelanceMarketBenchmarks: Record<string, BenchmarkData[]> = {
  "upwork-fiverr-decline": [
    {
      title: "Upwork: Active Clients & Revenue (2023–2025)",
      description: "Active client count and full-year revenue from Upwork's annual filings.",
      source: "Upwork SEC filings, Q4 2025 shareholder letter",
      sourceYear: "2025",
      columns: [
        { key: "year", label: "Year" },
        { key: "clients", label: "Active Clients", highlight: true },
        { key: "revenue", label: "Full-Year Revenue" },
        { key: "yoy", label: "YoY Change" },
      ],
      data: [
        { year: "2023", clients: "~853,000", revenue: "$689.0M", yoy: "+11%" },
        { year: "2024", clients: "~832,000", revenue: "$769.3M", yoy: "+12%" },
        { year: "2025", clients: "~785,000", revenue: "$787.8M", yoy: "+2%" },
      ],
    },
    {
      title: "Upwork (UPWK) Stock Performance",
      description: "Stock decline from 2021 peak through 2026.",
      source: "Yahoo Finance, TradingView, Macrotrends",
      sourceYear: "2026",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "value", label: "Value", highlight: true },
        { key: "context", label: "Context" },
      ],
      data: [
        { metric: "All-time high (Jul 2021)", value: "$60.70", context: "Pandemic-era freelance boom peak" },
        { metric: "Current price (May 2026)", value: "~$8", context: "Approx. -87% from peak" },
        { metric: "YTD 2026 return", value: "-56%", context: "Worst-performing year as public co." },
        { metric: "6-month return", value: "-49%", context: "Accelerating decline" },
        { metric: "1-day drop after Q4 2025 earnings", value: "-19%", context: "Feb 10, 2026 (Trefis, MarketBeat)" },
      ],
    },
    {
      title: "Fiverr (FVRR) Q4 2025 Snapshot",
      description: "Active buyers, revenue, guidance, and stock reaction.",
      source: "Fiverr Q4 2025 earnings release (Feb 18, 2026), StockStory, Parameter",
      sourceYear: "2026",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "value", label: "Value", highlight: true },
        { key: "note", label: "Note" },
      ],
      data: [
        { metric: "Active buyers YoY", value: "-13.6%", note: "Q4 2025 vs Q4 2024" },
        { metric: "Q4 2025 revenue", value: "$107.2M (+3.4% YoY)", note: "Growth slowing" },
        { metric: "2026 revenue guidance", value: "$380M – $420M", note: "vs ~$456.8M consensus (~-12% midpoint miss)" },
        { metric: "2026 Adjusted EBITDA guidance", value: "$60M – $80M", note: "Down from $91.6M in 2025" },
        { metric: "Stock reaction post-earnings", value: "-13% to -17%", note: "Feb 18, 2026" },
        { metric: "52-week range", value: "$9.67 – $34.13", note: "52-week low set Mar 30, 2026" },
        { metric: "Sep 2025 layoffs", value: "~250 employees (~30%)", note: "Pivot to 'AI-first' company" },
      ],
    },
    {
      title: "Why the Market Is Pricing In a Smaller Future",
      description: "Structural pressures on both platforms.",
      source: "Wharton (Manav Raj), Purposed, Freelance Informer",
      sourceYear: "2024–2026",
      columns: [
        { key: "pressure", label: "Structural Pressure" },
        { key: "impact", label: "Observed Impact", highlight: true },
      ],
      data: [
        { pressure: "Generative AI replacing commodity gigs", impact: "Writing, translation & basic code demand down post-ChatGPT (Wharton, 2024)" },
        { pressure: "Clients going direct to ChatGPT / Claude / Midjourney", impact: "Lower-end project volume shrinking on both platforms" },
        { pressure: "Freelancer backlash to Fiverr 'AI-first' Go pivot", impact: "Trust & supply-side concerns documented in 2025–2026 coverage" },
        { pressure: "Enterprise / agency move off open marketplaces", impact: "Buyers shifting to vetted agencies & fractional specialists" },
      ],
    },
  ],
};

// Append Freelancer Limited (FLN.AX) benchmark and a private-platform note
freelanceMarketBenchmarks["upwork-fiverr-decline"].push(
  {
    title: "Freelancer Limited (ASX: FLN) — FY25 Results",
    description:
      "Freelancer.com's parent reported FY25 results on 25 Feb 2026. Group GMV is shrinking, with revenue growth driven almost entirely by Escrow.com.",
    source: "Freelancer Limited FY25 Business Update & Appendix 4E (ASX)",
    sourceYear: "2025",
    columns: [
      { key: "metric", label: "Metric" },
      { key: "value", label: "FY25 Value", highlight: true },
      { key: "yoy", label: "YoY Change" },
    ],
    data: [
      { metric: "Group GMV", value: "A$881.5M", yoy: "-7.1%" },
      { metric: "Freelancer segment GMV", value: "A$133.4M", yoy: "+2.3%" },
      { metric: "Escrow.com GMV", value: "A$748.1M", yoy: "-8.6%" },
      { metric: "Group Revenue", value: "A$55.3M", yoy: "+4.1%" },
      { metric: "Freelancer segment Revenue", value: "A$40.9M", yoy: "+0.7%" },
      { metric: "Escrow.com Revenue (record)", value: "A$12.3M", yoy: "+18.8%" },
      { metric: "NPAT", value: "A$2.2M", yoy: "vs -A$0.8M in FY24" },
    ],
  },
  {
    title: "Freelancer (FLN.AX) Stock Performance",
    description:
      "Despite a return to profit, FLN has continued to derate in 2026 alongside UPWK and FVRR.",
    source: "ASX / MarketScreener",
    sourceYear: "2026",
    columns: [
      { key: "metric", label: "Metric" },
      { key: "value", label: "Value", highlight: true },
      { key: "context", label: "Context" },
    ],
    data: [
      { metric: "Share price (late Apr 2026)", value: "A$0.145", context: "Down from A$0.23 at start of 2026" },
      { metric: "YTD 2026 return", value: "~-37%", context: "MarketScreener, 30 Apr 2026" },
      { metric: "5-day change post-Q1 2026 update", value: "-14.7%", context: "Apr 22, 2026 Q1 earnings reaction" },
      { metric: "Q1 2026 commentary", value: "Lower revenue, positive NPAT", context: "Freelancer.com segment growth slowing" },
    ],
  },
  {
    title: "Private Freelance Platforms — Status Check",
    description:
      "Toptal, Guru and PeoplePerHour do not publish audited financials, but public signals point in the same direction as the listed players.",
    source: "Company sites, AgencyReview, press coverage",
    sourceYear: "2025–2026",
    columns: [
      { key: "platform", label: "Platform" },
      { key: "signal", label: "2025–2026 Signal", highlight: true },
      { key: "note", label: "Note" },
    ],
    data: [
      {
        platform: "Toptal",
        signal: "Repositioning around enterprise AI talent",
        note: "Private; Q4 2025 Talent Market Report shows softening demand for generalist roles, growth concentrated in AI/ML.",
      },
      {
        platform: "Guru.com",
        signal: "Static traffic, no new product narrative",
        note: "Private; long-tail SEO traffic in slow decline, no major 2025–2026 launches reported.",
      },
      {
        platform: "PeoplePerHour",
        signal: "Pivoted to AI-augmented gigs, shrinking buyer base",
        note: "Private UK platform; same pattern as Fiverr — leaning on AI tooling to defend against ChatGPT cannibalization.",
      },
    ],
  },
);

// AI cost / enterprise AI spend benchmarks
export const aiCostBenchmarks: Record<string, BenchmarkData[]> = {
  "microsoft-drops-claude-code": [
    {
      title: "Microsoft Claude Code Rollout → Rollback Timeline",
      description:
        "Six-month arc from Anthropic partnership launch to license cancellation, as reported by The Verge.",
      source: "The Verge (Tom Warren), Notepad newsletter",
      sourceYear: "2025–2026",
      columns: [
        { key: "date", label: "Date" },
        { key: "event", label: "Event", highlight: true },
        { key: "source", label: "Reporting" },
      ],
      data: [
        { date: "Dec 2025", event: "Microsoft opens Claude Code access to thousands of internal developers", source: "The Verge, Jan 22, 2026" },
        { date: "Jan 22, 2026", event: "'Claude Code is suddenly everywhere inside Microsoft' — favored over Copilot internally", source: "The Verge / Notepad" },
        { date: "Mar 9, 2026", event: "Microsoft launches Copilot Cowork in M365 E7 bundle ($30/user/mo), reselling Anthropic agentic tech", source: "Stratechery, Major Matters" },
        { date: "May 14, 2026", event: "Microsoft tells Experiences + Devices engineers Claude Code is being wound down by end of June", source: "The Verge / Notepad" },
        { date: "Jun 30, 2026", event: "Microsoft fiscal year end — internal Claude Code cutoff for E+D org", source: "Microsoft IR / The Verge" },
      ],
    },
    {
      title: "Who Is Affected at Microsoft",
      description:
        "The cut is scoped to the Experiences + Devices (E+D) organization — the engineers behind most of Microsoft's flagship end-user products.",
      source: "The Verge, ABP Live, Pomegra",
      sourceYear: "2026",
      columns: [
        { key: "item", label: "Item" },
        { key: "detail", label: "Detail", highlight: true },
      ],
      data: [
        { item: "Affected org", detail: "Experiences + Devices (E+D)" },
        { item: "Products in scope", detail: "Windows, Microsoft 365, Outlook, Microsoft Teams, Surface" },
        { item: "Reported headcount", detail: "~100,000 engineers (ABP Live, Pomegra, byteiota)" },
        { item: "Verge wording", detail: "'thousands' of Microsoft developers" },
        { item: "Replacement tool", detail: "GitHub Copilot CLI" },
        { item: "Cutoff", detail: "End of June 2026 (fiscal year end Jun 30)" },
        { item: "Stated reason", detail: "Convergence on Copilot CLI as agentic CLI standard" },
        { item: "Unstated reason (per Verge sources)", detail: "Financial — token-based Claude Code costs ran past forecast" },
      ],
    },
    {
      title: "Claude Code vs GitHub Copilot CLI — Why Convergence Matters",
      description:
        "Microsoft sells Copilot to its customers but its own engineers were preferring Anthropic's tool. The economics of that mismatch.",
      source: "The Verge, Stratechery, Major Matters, Anthropic & GitHub pricing pages",
      sourceYear: "2026",
      columns: [
        { key: "dim", label: "Dimension" },
        { key: "claude", label: "Claude Code", highlight: true },
        { key: "copilot", label: "GitHub Copilot CLI" },
      ],
      data: [
        { dim: "Vendor", claude: "Anthropic (3rd party)", copilot: "GitHub / Microsoft (1st party)" },
        { dim: "Billing model", claude: "Primarily token-based (API + Claude Pro/Max tiers)", copilot: "Per-seat subscription (Business $19, Enterprise $39/user/mo)" },
        { dim: "Cost predictability", claude: "Variable — scales with usage", copilot: "Fixed — predictable monthly spend" },
        { dim: "Microsoft margin", claude: "Cost center (pays Anthropic per token)", copilot: "Internal — cost absorbed in own infra" },
        { dim: "Strategic alignment", claude: "Undermines Copilot revenue story", copilot: "Flagship Microsoft AI product" },
        { dim: "Engineer preference (E+D, Q1 2026)", claude: "Reportedly preferred internally", copilot: "Adoption lagging until forced convergence" },
      ],
    },
    {
      title: "Industry Context: 2026 Enterprise AI Budget Pressure",
      description:
        "Microsoft is not alone. Multiple public companies signaled AI spend running ahead of plan in Q1–Q2 2026.",
      source: "The Verge, Reuters, public earnings commentary",
      sourceYear: "2026",
      columns: [
        { key: "company", label: "Company" },
        { key: "signal", label: "2026 Signal", highlight: true },
        { key: "source", label: "Source" },
      ],
      data: [
        { company: "Microsoft", signal: "Cancels most internal Claude Code licenses, sources cite financial reasons", source: "The Verge, May 14, 2026" },
        { company: "Uber", signal: "President: AI spending is 'getting harder to justify'", source: "The Verge, May 2026" },
        { company: "Anthropic", signal: "Reportedly raised pricing/limits on Claude Max plans amid heavy Claude Code usage", source: "Anthropic status & community reports, 2026" },
        { company: "GitHub / Microsoft", signal: "Pushes Copilot Cowork (M365 E7) at $30/user/mo — same Anthropic tech, fixed price", source: "Stratechery, Mar 9, 2026" },
      ],
    },
  ],
  "ai-cost-vs-employees": [
    {
      title: "MIT CSAIL: Where AI Beats Human Cost (and Where It Doesn't)",
      description:
        "Findings from 'Beyond AI Exposure: Which Tasks are Cost-Effective to Automate with Computer Vision?' (Svanberg, Li, Fleming, Goehring, Thompson — MIT IDE/CSAIL, January 2024).",
      source: "MIT CSAIL / MIT IDE working paper, Jan 2024",
      sourceYear: "2024",
      columns: [
        { key: "metric", label: "Metric" },
        { key: "value", label: "Finding", highlight: true },
      ],
      data: [
        { metric: "Share of worker pay exposed to AI vision automation", value: "~36% of US worker compensation" },
        { metric: "Share of that exposed pay where AI is cheaper than a human", value: "~23% (today's cost curves)" },
        { metric: "Implication", value: "For ~77% of vision-exposed work, humans remain the cheaper option" },
        { metric: "Time horizon for material change", value: "Years, even with rapid cost declines" },
        { metric: "Author quote", value: "'Machine learning is more expensive than the human alternative for the vast majority of jobs.'" },
      ],
    },
    {
      title: "Verified 2026 AI Cost Datapoints",
      description:
        "Public statements and reporting on real 2026 enterprise AI spend — not vendor marketing.",
      source: "Axios, The Verge, Reuters, Morgan Stanley, CNBC, MakeUseOf",
      sourceYear: "2026",
      columns: [
        { key: "fact", label: "Datapoint", highlight: true },
        { key: "source", label: "Primary source" },
      ],
      data: [
        { fact: "Nvidia VP Bryan Catanzaro: 'the cost of compute is far beyond the costs of the employees' for his team", source: "Axios (reported by Tom's Hardware, WCCFTech, IBTimes UK, FactCheckRadar)" },
        { fact: "Uber burned full 2026 AI tooling budget in ~4 months (by April)", source: "Business Tech News, Startup Fortune, Awesome Agents, BERI" },
        { fact: "Some Uber engineers spending $500–$2,000/month on AI coding tokens", source: "BERI, Startup Fortune" },
        { fact: "Microsoft cancels internal Claude Code for ~100K E+D engineers by Jun 30, 2026 — financial reasons cited", source: "The Verge (Tom Warren), May 14, 2026" },
        { fact: "Big Tech 2026 AI capex committed: ~$740B (Morgan Stanley) / ~$725B and +77% YoY (Wedbush via Tom's Hardware)", source: "Morgan Stanley, CNBC, Tom's Hardware, Business Insider" },
        { fact: "Reddit developer left Claude Code on a 30-min loop overnight — ~$6,000 bill by morning", source: "MakeUseOf, 2026" },
      ],
    },
    {
      title: "Salary vs Token Spend — Why the Cost Shapes Are Different",
      description:
        "Why 'AI is cheaper than a salary' is the wrong mental model for budgeting AI in 2026.",
      source: "IT Dukes analysis based on public pricing",
      sourceYear: "2026",
      columns: [
        { key: "dim", label: "Dimension" },
        { key: "salary", label: "Salaried employee", highlight: true },
        { key: "ai", label: "Token-billed AI" },
      ],
      data: [
        { dim: "Cost shape", salary: "Fixed monthly", ai: "Variable, scales with usage" },
        { dim: "Predictability", salary: "High — known 12 months ahead", ai: "Low — can 2–3× month-over-month" },
        { dim: "Marginal cost of 'one more task'", salary: "$0 (within capacity)", ai: ">$0 every time, no cap by default" },
        { dim: "Cost of 'left running by accident'", salary: "$0", ai: "Up to thousands per night (per MakeUseOf $6K incident)" },
        { dim: "Best fit", salary: "Recurring judgment-heavy work", ai: "Bursty, repetitive, machine-readable work" },
        { dim: "Procurement model that works", salary: "Headcount planning", ai: "Hard per-seat caps + monthly audits + fixed-seat tools where possible" },
      ],
    },
    {
      title: "Where AI Actually Wins Today (and Where It Doesn't)",
      description:
        "Rule of thumb from MIT CSAIL data and 2026 enterprise reporting.",
      source: "MIT CSAIL (2024), The Verge (2026), IT Dukes client work",
      sourceYear: "2024–2026",
      columns: [
        { key: "category", label: "Category" },
        { key: "verdict", label: "AI cheaper than human?", highlight: true },
        { key: "why", label: "Why" },
      ],
      data: [
        { category: "Bulk image/document classification at scale", verdict: "Yes", why: "High volume amortizes compute; humans are the bottleneck" },
        { category: "Code scaffolding & boilerplate", verdict: "Yes (with seat caps)", why: "Per-seat Copilot/Cursor pricing beats hourly dev rate" },
        { category: "24/7 tier-1 support deflection", verdict: "Yes", why: "Replaces shift coverage, fixed seat or fixed bot pricing" },
        { category: "Senior judgment / ambiguous decisions", verdict: "No", why: "Token cost of long-context reasoning exceeds salary equivalent" },
        { category: "Most vision-based tasks (per MIT)", verdict: "No (~77%)", why: "Build + deploy + maintain still exceeds human cost" },
        { category: "Agentic autonomous workflows running overnight", verdict: "No (yet)", why: "Per-token meter + reasoning loops = unpredictable spend spikes" },
      ],
    },
  ],
};

// Get all benchmarks for a specific insight
export const getBenchmarksForInsight = (slug: string): BenchmarkData[] => {
  return (
    emailDeliverabilityBenchmarks[slug] ||
    aiDevelopmentBenchmarks[slug] ||
    securityBenchmarks[slug] ||
    pmBenchmarks[slug] ||
    businessItBenchmarks[slug] ||
    freelanceMarketBenchmarks[slug] ||
    aiCostBenchmarks[slug] ||
    []
  );
};
